import React from 'react';

import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import FUND_ACCOUNT_SOURCES from '~/server_shared/generated-types/FUND_ACCOUNT_SOURCES';
import PAYMENT_PROVIDERS from '~/server_shared/generated-types/PAYMENT_PROVIDERS';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import type { FundAccountSourceConfig } from '../types';

import { useFundAccountRulesAvailability } from './useFundAccountRulesAvailability';

const FUND_ACCOUNT_SOURCE_FIELD_ID = 'method_configuration.fund_account.source';

/**
 * The source a configuration with no stored block reads as. It is what the method has always done — the
 * provider sends from its own configured account — so a legacy configuration shows its actual behaviour
 * rather than an unanswered radio group.
 */
export const DEFAULT_FUND_ACCOUNT_SOURCE: FundAccountSourceConfig['source'] = 'static';

/** The organization fields this gate reads, named because `useSysconfig` is an untyped JS context. */
export interface FundAccountSourceOrganization {
  id: number;
  features: { name: string }[];
}

interface FundAccountSourceContext {
  organization: FundAccountSourceOrganization;
  paymentProvider?: string | null;
}

/** Narrows a stored provider value to a key of the generated map; see the cast-free lookup below. */
const isKnownProvider = (paymentProvider?: string | null): paymentProvider is keyof typeof PAYMENT_PROVIDERS =>
  !!paymentProvider && paymentProvider in PAYMENT_PROVIDERS;

/**
 * Whether the provider routing this method sends from a fund account of its own — i.e. whether the block
 * belongs on this configuration at all.
 *
 * Derived from the provider's own `is_fund_account_required` flag rather than from a provider's name, so
 * a second provider that sends from a configured account gets the choice by declaring the flag. A method
 * with no provider has no static account to choose from — the engine simply runs for it.
 *
 * Deliberately flag-free, unlike `isFundAccountSourceConfigurable` below. A stored block outlives the
 * feature flag, so *whether the key belongs here* has to be answerable with the flag off: the provider
 * switch removes the block when the next provider keeps no fund account, and reading the flag there
 * would delete a persisted block on every round trip through another provider while the engine is off.
 */
export const doesProviderKeepFundAccount = (paymentProvider?: string | null): boolean => {
  // Narrowed rather than cast: '' is the stored value for "no provider" (see `PaymentProviderSelect`)
  // and the field is nullable, neither of which is a key of the generated map.
  if (!isKnownProvider(paymentProvider)) {
    return false;
  }
  const providerDetails = PAYMENT_PROVIDERS[paymentProvider];
  // Only the providers that keep one declare the key, hence the `in` check rather than a lookup: the
  // generated map is `as const`, and a provider with no fund account is an empty object in it.
  return 'is_fund_account_required' in providerDetails && providerDetails.is_fund_account_required;
};

/**
 * Whether this method has a fund-account source to *choose* at all — the gate on the radio itself.
 *
 * The provider half above, gated on the feature flag as well, and that half is load-bearing rather than
 * cosmetic: the server only relaxes the provider's account-id rule for a flag-on organization, so
 * offering the choice with the flag off would invite a save it then rejects.
 *
 * Exported because the mount and the initial-values layer have to agree: a block seeded where no radio
 * renders is unreachable configuration, and a radio with no block behind it shows no selection. The
 * provider switch reads the two halves separately — see `doesProviderKeepFundAccount`.
 */
export const isFundAccountSourceConfigurable = ({ organization, paymentProvider }: FundAccountSourceContext): boolean =>
  doesProviderKeepFundAccount(paymentProvider) &&
  isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.FUND_ACCOUNT_ENGINE);

/**
 * Whether the engine is the thing actually resolving this method's account, which is what decides
 * whether the routing provider's own account field is still the one in use.
 *
 * Gated on the flag as well as on the block, because a stored block outlives the flag: a method saved
 * as rules-sourced and then left with the engine switched off is resolved by nobody, so the provider's
 * configured account is the account again. The server says the same in both directions —
 * `account_id_resolved_by_rules` relaxes its account-id rule only when the flag is on *and* the block
 * says rules (`payments_config_manager`), and `resolve_payment_fund_account` returns nothing at all
 * with the flag off (`payments_config.internal.service`). Reading the block alone here would hide the
 * field and drop its Yup rule while the server still demanded the value, i.e. a 400 on a field that is
 * not on screen, escapable only by switching provider away and back.
 */
export const isFundAccountResolvedByRules = ({
  organization,
  fundAccount,
}: {
  organization: FundAccountSourceOrganization;
  fundAccount?: FundAccountSourceConfig | null;
}): boolean =>
  fundAccount?.source === 'rules' && isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.FUND_ACCOUNT_ENGINE);

/**
 * The initial-values merge layer for the fund-account block, shaped as the nested document rather than
 * the block itself so a persisted block still wins over the default — the same reason the provider
 * descriptors shape theirs that way.
 */
export const getDefaultFundAccountConfig = (
  context: FundAccountSourceContext
): { method_configuration?: { fund_account: FundAccountSourceConfig } } =>
  isFundAccountSourceConfigurable(context)
    ? { method_configuration: { fund_account: { source: DEFAULT_FUND_ACCOUNT_SOURCE } } }
    : {};

const RULES_UNAVAILABLE_NOTE =
  "Set the fund-account engine's default behaviour and enable at least one rule covering this payment method first.";

interface FundAccountSourceFieldProps {
  organizationId: number;
  paymentMethod: string;
  /** The configuration row's sub-organization; absent on the org-level row. */
  subOrganizationId?: number | null;
  readOnly?: boolean;
  disabled?: boolean;
}

/**
 * Where this payment method's fund account comes from: the provider's own configured account, or the
 * fund-account engine's rules resolving one per payment.
 *
 * Lives in the generic payments-configuration layer rather than inside a provider's own folder,
 * because the value it writes — `method_configuration.fund_account` — is a provider-agnostic key the
 * `fund_accounts` module owns. Mounted only where `isFundAccountSourceConfigurable` above says the
 * choice exists, so the readiness fetch never fires for a provider that keeps no fund account or for
 * an organization without the engine.
 *
 * Readiness is asked per (sub-org × method), which is the granularity the configuration rows have and
 * the granularity the resolver narrows by — an org whose only enabled rule targets another sub-org must
 * not be offered "Use rules" on this one.
 *
 * The options are built from the generated enum, so a third source is a line of enum data rather
 * than a branch here — and cannot drift from what the backend's `extra='forbid'` block accepts. The
 * readiness gate travels with that vocabulary too: a source is gated because its own entry declares
 * `requires_engine_readiness`, not because this file names it, so a third engine-served source is
 * gated by the same line of enum data that adds it.
 */
function FundAccountSourceField({
  organizationId,
  paymentMethod,
  subOrganizationId,
  readOnly,
  disabled,
}: FundAccountSourceFieldProps) {
  const { isRulesSourceAvailable } = useFundAccountRulesAvailability(organizationId, paymentMethod, subOrganizationId);

  const options = Object.entries(FUND_ACCOUNT_SOURCES).map(([sourceKey, sourceDetails]) => {
    // `in` rather than a lookup, as on the provider map above and for the same reason: the generated
    // map is `as const`, so only the entries that declare the key carry it.
    const requiresEngineReadiness =
      'requires_engine_readiness' in sourceDetails && sourceDetails.requires_engine_readiness;
    const isUnavailable = requiresEngineReadiness && !isRulesSourceAvailable;
    return {
      optionValue: sourceKey,
      text: sourceDetails.desc,
      disabled: isUnavailable,
      // The group's own per-option contract, rather than a tooltip wrapping the whole field: a wrapper
      // pops the same message over the enabled option, which it does not explain. Above the button
      // rather than trailing it, so the long message does not stretch the option into the row beside it.
      note: isUnavailable ? RULES_UNAVAILABLE_NOTE : undefined,
      noteLocation: 'top' as const,
    };
  });

  return (
    // Unlabelled: the section card this field is mounted in carries the "Fund account" heading, and a
    // second title inside the group would double it.
    <RadioButtonGroupFormik
      id={FUND_ACCOUNT_SOURCE_FIELD_ID}
      options={options}
      // `showOnly` is what the group disables every option with — there is no separate `disabled`
      // prop — so read-only and mid-submit both come through it, as they do on the other fields here.
      showOnly={readOnly || disabled}
    />
  );
}

export default FundAccountSourceField;
